import classNames from "classnames";
import { Field, Form, useField, useFormikContext } from "formik";
import {
  HTMLInputTypeAttribute,
  ReactNode,
  useCallback,
  useState,
} from "react";
import toast from "react-hot-toast";
import Modal from "react-modal";
import { Link, useSearchParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";

import {
  Accession,
  AccessionPATCH,
  Case,
  Clinic,
  InsurancePlan,
  Patient,
  PatientGuarantor,
  Specimen,
  SpecimenProtocolOverride,
  User,
  UserName,
} from "pwapi";

import { UserLocation } from "custom-models";

import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { selectExternalLabsForLab } from "shared/selectors/externalLabSelectors";
import {
  getInitialAccessionBillingType,
  getUpdatedBillingType,
} from "shared/utils/rulesEngineUtils";
import { getProtocolStatus } from "shared/utils/specimenUtils";
import {
  flattenObjectToArray,
  requiredError,
} from "shared/utils/validationUtils";

import { useAppSelector } from "app/hooks";
import { AccessionState } from "app/reducers/accessionReducer";
import { useAllowsExternalAccession } from "hooks/useAllowsExternalAccession";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import SelectSearchInput from "ui/CaseFormInputs/SelectSearchInput";
import useCaseInputs from "ui/CaseFormInputs/useCaseInputs";
import CaseDetailsForm from "ui/CaseForms/CaseDetailsForm";
import InsuranceForm from "ui/CaseForms/InsuranceForm";
import IntegrationsForm from "ui/CaseForms/IntegrationsForm";
import PatientForm from "ui/CaseForms/PatientForm";
import PatientGuarantorForm from "ui/CaseForms/PatientGuarantorForm";
import Spinner from "ui/components/Spinner";
import { compareCaseToModMed } from "util/caseUtils";
import { ModMedPWPayload, accessionFromPayload } from "util/objectFromBarcode";

import AccessionSidebar from "./accessionSidebar";

export const AccessionSelect = ({
  name,
  filter,
  filters,
  label,
  serviceFunction,
  mapToOptions,
  component,
  debounceTime,
  inputLimit,
  handleOnChange,
  handleOnBlur,
  required = false,
  disabled = false,
  children,
}: {
  name: string;
  filter: string;
  filters?: {};
  label: string;
  serviceFunction: Function;
  mapToOptions: Function;
  component: ReactNode;
  debounceTime?: number;
  inputLimit?: number;
  handleOnBlur?: Function;
  handleOnChange?: Function;
  required?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) => {
  // Meta is an unused variable
  // eslint-disable-next-line
  const [field, meta, { setValue, setTouched }] = useField(name);

  return (
    <>
      <SelectSearchInput
        onChange={(selectedOption: any) => {
          handleOnChange
            ? handleOnChange(selectedOption.object)
            : setValue(selectedOption.object);
          setTouched(true);
          handleOnBlur && handleOnBlur(selectedOption.object);
        }}
        onBlur={() => {
          setTouched(true);
        }}
        serviceOptions={filters}
        apiService={serviceFunction}
        field={name}
        label={label}
        filter={filter}
        component={component}
        mapToOptions={mapToOptions}
        debounceTime={debounceTime}
        inputLimit={inputLimit}
        disabled={disabled}
      >
        {children}
      </SelectSearchInput>
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ opacity: 0, height: 0, position: "absolute", bottom: 0 }}
          onChange={() => {}}
          value={field.value || ""}
          required
        />
      )}
    </>
  );
};

// TODO: can be improved by taking more out of HTML Element Attribute types
// ... and passing those down with a spread, so not every possible attribute
// ... needs to be recreated individually
export const Input = ({
  name,
  value,
  checked,
  className,
  label,
  labelDetails,
  placeholder,
  type,
  disabled,
  required = false,
  handleOnClick,
  handleOnBlur,
  minLength,
  maxLength,
  max,
  min,
  error,
  touched,
  warning,
  formatter,
}: {
  name: string;
  value?: string;
  checked?: boolean;
  className?: string;
  label?: string;
  labelDetails?: ReactNode;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  disabled?: boolean;
  required?: boolean;
  handleOnClick?: Function;
  handleOnBlur?: Function;
  minLength?: number;
  maxLength?: number;
  max?: string | number | null;
  min?: string | number | null;
  error?: string;
  touched?: boolean;
  warning?: string;
  formatter?: Function;
}) => {
  // eslint-disable-next-line
  const [field, meta, { setValue, setTouched }] = useField(name);
  const parts = name.split(".");
  const last = parts[parts.length - 1];
  const displayLabel = label || last?.split("_").join(" ");

  return (
    <div
      className={classNames("Accession__input", className, {
        "Accession__input--error": error && touched && !requiredError(error),
        "Accession__input--submission-check-error": error,
        "Accession__input--warning": !error && touched && warning,
        "Accession__input--required": required,
      })}
    >
      <label htmlFor={`accession-${name}`}>
        {displayLabel}
        {required && " *"}
        {labelDetails && <div>{labelDetails}</div>}
      </label>

      <Field
        type={type}
        name={name}
        id={`accession-${name}`}
        autoComplete={"do-not-autocomplete"}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        checked={checked}
        onChange={(e: any) => {
          if (formatter) {
            const formatted = formatter(
              type === "checkbox" ? e.target.checked : e.target.value
            );
            setValue(formatted);
          } else {
            setValue(type === "checkbox" ? e.target.checked : e.target.value);
          }
        }}
        onClick={
          handleOnClick &&
          ((e: any) => {
            handleOnClick(e.target.value);
          })
        }
        onBlur={(e: any) => {
          setTouched(true);
          handleOnBlur && handleOnBlur(e.target.value);
        }}
        minLength={minLength}
        maxLength={maxLength}
        max={max}
        min={min}
        {...(value && { value: value })}
      />
    </div>
  );
};

export const initialValuesFromParams = (params: URLSearchParams) => {
  return {
    patient: {
      first_name: params.get("first_name"),
      last_name: params.get("last_name"),
      date_of_birth: params.get("date_of_birth"),
      external_patient_id: params.get("external_patient_id"),
      medical_record_number: params.get("medical_record_number"),
    },
    specimen: [
      {
        name: params.get("name"),
        site: params.get("location"),
      },
    ],
    collected_date: params.get("collected_date"),
  };
};

export const blankInsurance = {
  relationship_to_patient: "",
  insured_first_name: "",
  insured_last_name: "",
  insured_date_of_birth: undefined,
  group_employer_id: "",
  policy_number: "",
  insurance_company_name: "",
  insurance_company_city: "",
  insurance_company_state: "",
  insurance_company_zipcode: "",
  planDetails: undefined,
  plan: undefined,
  has_abn: false,
  preapproval_id: "",
};

export const blankGuarantor = {
  first_name: "",
  last_name: "",
  middle_initial: "",
  ssn: "",
  date_of_birth: "",
  sex: "",
  address: {
    street: "",
    street2: "",
    city: "",
    state: "",
    zipcode: "",
    phone1: "",
    phone2: "",
  },
};

const AccessionForm = ({
  accession,
  onHandlePatchAccession: onHandlePatchAccessionFieldMeta,
  warnings = {},
}: {
  accession?: AccessionState;
  onHandlePatchAccession: (
    values: Partial<AccessionPATCH>,
    setBillerTypeDefaults?: boolean
  ) => Promise<Accession> | undefined;
  warnings?: { [key: string]: string };
}) => {
  const lab = useSelectedLab();
  const externalLabs = useAppSelector((state) =>
    selectExternalLabsForLab(state, lab)
  );
  const externalGrossingLab = useAppSelector((state) =>
    selectFeatureFlag(state, "externalGrossingLab")
  );

  const caseTypeFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "caseType")
  );

  const { priority, grossedByExternalLab, caseType } = useCaseInputs();

  const clinics = useAppSelector((state) => state.clinics);

  // The URL gets an ?integration=modmed if it was kickstarted
  const [searchParams] = useSearchParams();
  const forceModMed = searchParams.get("integration") === "modmed";

  const {
    errors,
    handleSubmit,
    setFieldValue,
    setValues,
    values,
    touched,
    setTouched,
  } = useFormikContext<
    AccessionPATCH & {
      // These are additional fields of the "integration" form
      ordering_doctor?: UserName;
      supervising_physician?: UserName;
      clinic?: Clinic;
      external_patient_id?: number | string;
    }
  >();
  const [showWarning, setShowWarning] = useState(false);
  const [formSubmissionCheck, setformSubmissionCheck] = useState(false);
  const warningList = flattenObjectToArray(warnings);

  const onHandlePatchAccession = lab.settings?.accession_field_meta?.active
    ? (values: Partial<AccessionPATCH>, setBillerTypeDefaults?: boolean) => {
        const field_meta = accession?.field_meta || {};
        return onHandlePatchAccessionFieldMeta(
          values,
          setBillerTypeDefaults
        )?.then((accession: AccessionState) => {
          Object.keys(accession?.field_meta || field_meta || {}).forEach((k) =>
            // @ts-ignore (need to trust the backend here)
            setFieldValue(k, accession[k])
          );
          return accession;
        });
      }
    : onHandlePatchAccessionFieldMeta;

  const onHandlePatchAccessionPatient = (object: Patient) => {
    onHandlePatchAccession({ patient: object });
  };

  const onHandlePatchAccessionInsurance = (objects: any[]) => {
    onHandlePatchAccession({ insurance: objects });
  };

  const onHandlePatchAccessionAddress = (object: UserLocation) => {
    onHandlePatchAccession({ address: object });
  };

  const onHandlePatchAccessionSpecimen = (objects: Specimen[]) => {
    onHandlePatchAccession({ specimens: objects });
  };

  const onHandlePatchAccessionGuarantor = (
    object: AccessionPATCH["guarantor"] = {}
  ) => {
    onHandlePatchAccession({
      guarantor: object,
    });
  };

  const onScanModMedSpecimen = useCallback(
    (mod_med_payload: ModMedPWPayload) => {
      if (accession && compareCaseToModMed(accession, mod_med_payload)) {
        const specimen = accession.specimens?.find(
          // TODO: `s` is not _really_ a Specimen, but because specimens
          // ...   is a json blob on Accession, it's `any`
          (s: Specimen) => s.name === mod_med_payload.specimen_name
        );
        if (!specimen) {
          // Add additional specimen to case
          const newSpecimen =
            accessionFromPayload(mod_med_payload)?.specimens?.at(0);
          const specimens = [...(accession?.specimens || []), newSpecimen];
          setFieldValue("specimens", specimens);
          onHandlePatchAccession({ specimens });
        } else {
          toast.error(
            `Specimen ${specimen.name} already appears to have been added`
          );
        }
      } else {
        toast.error(
          `Specimen ${mod_med_payload.specimen_name} may not belong to case`
        );
      }
    },
    [accession, setFieldValue]
  );

  useBarcodeScanner((barcode) => {
    if (barcode.object_type === "specimen" && barcode.mod_med_payload) {
      onScanModMedSpecimen(barcode.mod_med_payload);
    }
  });

  const atLeastOneSpecimen = values.specimens?.length > 0;
  const hasAllSpecimenInformation = values.specimens?.every(
    (s: Specimen) =>
      s.type &&
      s.site &&
      s.procedure &&
      (lab?.settings?.transcription_entry?.active || s.clinical_history)
  );
  const readyToSubmit = atLeastOneSpecimen && hasAllSpecimenInformation;
  const hasSlowMohsSpecimen = values.specimens.some(
    (s: Specimen) =>
      getProtocolStatus(s, SpecimenProtocolOverride.protocol.SLOW_MOHS).active
  );

  const allowExternalAccession = useAllowsExternalAccession({
    clinic: accession?.clinic,
    grossed_by_external_lab_id: accession?.grossed_by_external_lab_id,
    accessioning_lab: lab,
    case_type: accession?.case_type,
    remains_digital_pc: accession?.remains_digital_pc,
  });

  const submissionCheck = (e: React.MouseEvent<HTMLButtonElement>) => {
    const hasErrors = Object.keys(errors).length > 0;

    if (hasErrors) {
      setformSubmissionCheck(true);

      return;
    }

    if (warningList.length > 0) {
      // If there are warnings, show the modal
      e.preventDefault();
      setShowWarning(true);
    }
  };

  if (!accession?.id) return <Spinner />;
  return (
    <div className="Accession">
      <Form autoComplete="off">
        <div
          className={classNames(
            "LISForm",
            formSubmissionCheck && "LISForm--submission-check"
          )}
        >
          <h1 className="LISForm__title">
            <Link to="/cases">
              <figure className="Logo">
                <img src="/logo.svg" alt="PathologyWatch" />
              </figure>
            </Link>
            <div>
              New Accession{" "}
              {accession?.prefix?.prefix && !allowExternalAccession && (
                <>
                  &mdash; (
                  <strong>
                    <code>{accession?.prefix?.prefix}</code>
                  </strong>{" "}
                  Prefix)
                </>
              )}
            </div>

            <div className="LISForm__title__inputs">
              {caseTypeFlag && (
                <div>
                  <label htmlFor={caseType.id}>{caseType.label}</label>
                  <Field
                    {...caseType}
                    as="select"
                    style={{ width: "100%" }}
                    onBlur={
                      onHandlePatchAccession &&
                      ((e: React.ChangeEvent<HTMLSelectElement>) => {
                        const value = e.target.value;
                        onHandlePatchAccession({
                          case_type: value as AccessionPATCH.case_type,
                        });
                      })
                    }
                  >
                    {caseType.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Field>
                </div>
              )}
              {externalGrossingLab && !!externalLabs?.length && (
                <div>
                  <label htmlFor={grossedByExternalLab.id}>
                    {grossedByExternalLab.label}
                  </label>
                  <Field
                    {...grossedByExternalLab}
                    as="select"
                    style={{ width: "100%" }}
                    // TODO: the fact that this needs to be onBlur and not onChange
                    // points to some pretty deep problems with our form management.
                    // What happens here if it's "onChange" is that it will "stick"
                    // to the previous value if you choose the blank value from the
                    // top of the list. Not sure why, but needs to be fixed.
                    onBlur={
                      onHandlePatchAccession &&
                      // TODO: e: should infer automatically
                      ((e: React.ChangeEvent<HTMLSelectElement>) => {
                        // TODO: This this should be common code DRY'd up somewhere
                        const value = e.target.value;
                        const grossed_by_external_lab_id = value
                          ? Number.isNaN(Number(value))
                            ? undefined
                            : Number(value)
                          : null;

                        onHandlePatchAccession({
                          grossed_by_external_lab_id,
                        });
                      })
                    }
                  >
                    <option key="none" value=""></option>
                    {externalLabs.map((externalLab) => (
                      <option key={externalLab.id} value={externalLab.id}>
                        {externalLab.name}
                      </option>
                    ))}
                  </Field>
                </div>
              )}
              <div>
                <label>{priority.label}</label>
                <Field
                  {...priority}
                  as="select"
                  style={{ width: "100%" }}
                  onBlur={
                    onHandlePatchAccession &&
                    ((e: Event) => {
                      onHandlePatchAccession({
                        priority: (e.target as HTMLInputElement)
                          .value as AccessionPATCH.priority,
                      });
                    })
                  }
                  disabled={hasSlowMohsSpecimen}
                  /* priority will be automatically set to stat on the backend when a Slow Mohs Specimen is present, */
                  /* so we don't need to actually change the value in values we just display what it is going to be  */
                  value={
                    hasSlowMohsSpecimen ? Case.priority.STAT : values.priority
                  }
                  data-tip={
                    hasSlowMohsSpecimen
                      ? "Priority is automatically set to 'stat' when a Slow Mohs Specimen is present"
                      : undefined
                  }
                  required
                >
                  {priority.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Field>
                <ReactTooltip />
              </div>
            </div>
          </h1>
          <IntegrationsForm
            onHandlePatchAccession={onHandlePatchAccession}
            initialClinic={accession?.clinic}
            onChangeClinic={(value: Clinic) => {
              const possibleDefaultBillingType = getInitialAccessionBillingType(
                value,
                clinics
              );

              const updatedBillingType = getUpdatedBillingType({
                initialBillingType: values.billing_type || undefined,
                availableBillingTypes:
                  clinics[value.id || 0].billing_types || [],
              });

              setValues({
                ...values,
                clinic: value,
                collected_date_timezone: value.location?.timezone,
                external_ezderm_order_id: "",
                billing_type: (values.billing_type &&
                value.id &&
                clinics?.[value.id].billing_types &&
                updatedBillingType
                  ? updatedBillingType
                  : possibleDefaultBillingType) as
                  | AccessionPATCH.billing_type
                  | undefined,
                ...(forceModMed
                  ? {}
                  : {
                      external_order_id: "",
                      external_patient_id: "",
                    }),
                ...(value?.id !== values.clinic?.id && {
                  supervising_physician: undefined,
                }),
              });
            }}
            onChangeOrderingDoctor={(value: User) => {
              const ordering_doctor_id = value?.id;
              const skip_provider_ids = values?.clinic?.emr_mappings?.flatMap(
                (em) => em?.settings?.skip_provider_ids || []
              );

              const clearIntegrationFields =
                ordering_doctor_id &&
                skip_provider_ids?.includes(ordering_doctor_id);

              setValues({
                ...values,
                ordering_doctor_id: value.id,
                ordering_doctor: value,
                ...(clearIntegrationFields
                  ? {
                      external_order_id: "",
                      external_patient_id: "",
                      external_ezderm_order_id: "",
                      patient: {
                        ...values.patient,
                        external_patient_id: "",
                      },
                    }
                  : {}),
              });
            }}
            clearValues={() => {
              // Clear all values and touch states
              setTouched({
                ...touched,
                clinic: false,
                ordering_doctor: false,
                supervising_physician: false,
                external_ezderm_order_id: false,
                external_order_id: false,
                external_patient_id: false,
              });
              setValues({
                ...values,
                clinic: undefined,
                ordering_doctor: undefined,
                supervising_physician: undefined,
                external_ezderm_order_id: "",

                ...(forceModMed
                  ? {}
                  : {
                      external_order_id: "",
                      external_patient_id: "",
                    }),
              });
            }}
            values={values}
            errors={errors}
            touched={touched}
            forceModMed={forceModMed}
            flatIntegration={false}
            skipMRN
          />
          <CaseDetailsForm
            values={values}
            errors={errors}
            warnings={warnings}
            touched={touched}
            setValues={setValues}
            editingAccession={accession}
            field_meta={
              lab?.settings?.accession_field_meta
                ? accession?.field_meta
                : undefined
            }
            remains_digital_pc={accession?.remains_digital_pc}
            onHandlePatchAccession={onHandlePatchAccession}
          />
          <PatientForm
            caseType={values.case_type}
            values={{ ...values.patient, address: values.address }}
            billingType={values.billing_type}
            errors={errors}
            warnings={warnings}
            touched={touched}
            onHandlePatchAccessionAddress={onHandlePatchAccessionAddress}
            onHandlePatchAccessionPatient={onHandlePatchAccessionPatient}
            patientInputPrefix="patient"
            currClinic={values.clinic}
            orderingDoctor={values.ordering_doctor}
            thirdPartyRequireLocationFields={
              lab?.settings?.third_party_require_location_fields?.active
            }
            requireStateField={lab?.settings?.billing_entry?.active}
          />
          <PatientGuarantorForm
            values={values.guarantor?.[0]}
            billingType={values.billing_type}
            onHandlePatchAccessionGuarantor={(
              values: Partial<PatientGuarantor>
            ) => {
              onHandlePatchAccessionGuarantor(values);
            }}
            onHandleChangeRelationship={(relationship: string) => {
              relationship === "Self"
                ? setFieldValue("guarantor", [
                    {
                      ...(values?.guarantor?.[0] || {}),
                      relationship_to_parent: relationship,
                    },
                  ])
                : setFieldValue("guarantor", [
                    {
                      ...blankGuarantor,
                      relationship_to_patient: relationship,
                    },
                  ]);
            }}
            guarantorInputPrefix="guarantor[0]"
            thirdPartyRequireLocationFields={
              lab?.settings?.third_party_require_location_fields?.active
            }
          />
          {values.insurance?.map((insurance: any, i: number) => (
            <InsuranceForm
              key={i}
              i={i}
              errors={errors}
              touched={touched}
              warnings={warnings}
              insurance={insurance}
              primary={i === 0}
              onBlur={() => {
                const newInsurArr = [...values.insurance];
                newInsurArr[i] = {
                  ...values.insurance[i],
                  // plan should not be added to the API call if using manual insurance
                  plan: lab.use_manual_insurance
                    ? undefined
                    : values.insurance[i].plan?.id
                    ? values.insurance[i].plan
                    : null,
                };
                onHandlePatchAccessionInsurance(newInsurArr);
              }}
              onHandleChangeInsurancePlan={(plan?: InsurancePlan) => {
                const newInsurArr = [...values.insurance];
                newInsurArr[i] = {
                  ...values.insurance[i],
                  plan: plan?.id ? plan : null,
                };
                onHandlePatchAccessionInsurance(newInsurArr);
              }}
              onHandleChangeRelationship={(relationship: string) => {
                const newInsurArr = [...values.insurance];
                newInsurArr[i] = {
                  ...values.insurance[i],
                  ...(relationship === "SELF"
                    ? {
                        insured_date_of_birth: undefined,
                        insured_first_name: "",
                        insured_last_name: "",
                      }
                    : {}),
                  relationship_to_patient: relationship,
                };
                setValues({
                  ...values,
                  insurance: newInsurArr,
                });
              }}
              usePrefix
              useInsurancePlan={!lab.use_manual_insurance}
            />
          ))}

          <div style={{ padding: "1em 0" }}>
            <button
              type="button"
              className="Button Button--purple"
              onClick={() => {
                setFieldValue("insurance", [
                  ...values.insurance,
                  blankInsurance,
                ]);
              }}
              disabled={values.insurance.length >= 5}
            >
              Add Additional Insurance
            </button>
            &nbsp;
            <button
              type="button"
              className="Button Button--purple"
              onClick={() => {
                setFieldValue("insurance", values.insurance.slice(0, -1));
                onHandlePatchAccessionInsurance(values.insurance.slice(0, -1));
              }}
              disabled={values.insurance.length <= 1}
            >
              Remove Additional Insurance
            </button>
          </div>
        </div>
        <button
          type="submit"
          className="Button Button--blue Accession__submit"
          disabled={!readyToSubmit}
          onClick={submissionCheck}
        >
          Accession Case
        </button>
        <Modal
          id="accession-warning-modal"
          isOpen={showWarning}
          onRequestClose={() => setShowWarning(false)}
          contentLabel="Are you sure you want to accession the case?"
          className="Modal"
          overlayClassName="Overlay"
        >
          <h3>Are you sure you want to accession the case?</h3>
          <p>The following case data may require review:</p>
          <ul className="LISForm__errorsList">
            {warningList.map((warning) => (
              <li key={`warning-list-item-${warning.id}`}>{warning.message}</li>
            ))}
          </ul>
          <p>Are you sure this data is correct?</p>
          <footer className="Modal__footer">
            <button className="Button" onClick={() => handleSubmit()}>
              Accession Case
            </button>
            <button className="Button" onClick={() => setShowWarning(false)}>
              Cancel
            </button>
          </footer>
        </Modal>
      </Form>
      <AccessionSidebar
        onSave={(
          specimen: Specimen,
          index?: number,
          slowMohsSegmentCount?: number
        ) => {
          const specimensCopy = [...values.specimens];
          if (index || index === 0) {
            specimensCopy[index] = specimen;
          } else {
            specimensCopy.push(specimen);
          }

          if (
            getProtocolStatus(
              specimen,
              SpecimenProtocolOverride.protocol.SLOW_MOHS
            ).active &&
            slowMohsSegmentCount
          ) {
            // Create new Specimens from initial Slow Mohs Specimen

            const curCharCode = specimen.name.charCodeAt(0);
            for (let i = 1; i < slowMohsSegmentCount; i++) {
              specimensCopy.push({
                ...specimen,
                name: String.fromCharCode(curCharCode + i),
                slow_mohs_source_name: specimen.name,
              });
            }
          }

          setFieldValue("specimens", specimensCopy);
          onHandlePatchAccessionSpecimen(specimensCopy);
        }}
        onDelete={({ index }: { index: number }) => {
          let specimensCopy = [
            ...values.specimens.slice(0, index),
            ...values.specimens.slice(index + 1),
          ];
          setFieldValue("specimens", specimensCopy);
          onHandlePatchAccessionSpecimen(specimensCopy);
        }}
        specimens={values.specimens}
      />
    </div>
  );
};

export default AccessionForm;
